import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@cieda/djayma-ui";
import {
  CheckCircle,
  XCircle,
  Truck,
  PackageCheck,
  Printer,
  RotateCcw,
} from "lucide-react";
import { AdminModal } from "@/components/admin/shared";
import type { OrderStatus } from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// Status-dependent actions
// ---------------------------------------------------------------------------

interface ActionDef {
  readonly targetStatus: OrderStatus;
  readonly labelKey: string;
  readonly icon: React.ReactNode;
  readonly variant: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  readonly className?: string;
}

const STATUS_ACTIONS: Readonly<Record<string, readonly ActionDef[]>> = {
  pending: [
    {
      targetStatus: "confirmed",
      labelKey: "order_detail.confirm_order",
      icon: <CheckCircle className="h-4 w-4" />,
      variant: "default",
      className: "bg-emerald-600 hover:bg-emerald-700 text-white",
    },
    {
      targetStatus: "cancelled",
      labelKey: "order_detail.cancel_order",
      icon: <XCircle className="h-4 w-4" />,
      variant: "destructive",
    },
  ],
  confirmed: [
    // Backend requires confirmed → processing → shipped (see
    // VALID_TRANSITIONS in apps/distribution-api/.../orders/[id]/status/route.ts).
    // Jumping directly to "shipped" returns 422.
    {
      targetStatus: "processing",
      labelKey: "order_detail.process_order",
      icon: <Truck className="h-4 w-4" />,
      variant: "default",
      className: "bg-blue-600 hover:bg-blue-700 text-white",
    },
    {
      targetStatus: "cancelled",
      labelKey: "order_detail.cancel_order",
      icon: <XCircle className="h-4 w-4" />,
      variant: "destructive",
    },
  ],
  processing: [
    {
      targetStatus: "shipped",
      labelKey: "order_detail.ship_order",
      icon: <Truck className="h-4 w-4" />,
      variant: "default",
      className: "bg-blue-600 hover:bg-blue-700 text-white",
    },
    {
      targetStatus: "cancelled",
      labelKey: "order_detail.cancel_order",
      icon: <XCircle className="h-4 w-4" />,
      variant: "destructive",
    },
  ],
  shipped: [
    {
      targetStatus: "delivered",
      labelKey: "order_detail.deliver_order",
      icon: <PackageCheck className="h-4 w-4" />,
      variant: "default",
      className: "bg-emerald-600 hover:bg-emerald-700 text-white",
    },
  ],
};

// ---------------------------------------------------------------------------
// Human-readable status labels in French
// ---------------------------------------------------------------------------

const STATUS_DISPLAY: Readonly<Record<string, string>> = {
  pending: "En attente",
  confirmed: "Confirmée",
  processing: "En préparation",
  shipped: "Expédiée",
  delivered: "Livrée",
  cancelled: "Annulée",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface OrderActionsProps {
  readonly currentStatus: string;
  readonly isUpdating: boolean;
  readonly onStatusChange: (newStatus: OrderStatus) => void;
  readonly onPrintDeliverySlip: () => void;
  readonly onInitiateRefund: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderActions({
  currentStatus,
  isUpdating,
  onStatusChange,
  onPrintDeliverySlip,
  onInitiateRefund,
}: OrderActionsProps) {
  const { t } = useTranslation("admin");
  const [confirmTarget, setConfirmTarget] = useState<OrderStatus | null>(null);

  const actions = STATUS_ACTIONS[currentStatus] ?? [];

  const handleConfirm = useCallback(() => {
    if (confirmTarget) {
      onStatusChange(confirmTarget);
      setConfirmTarget(null);
    }
  }, [confirmTarget, onStatusChange]);

  const handleCancel = useCallback(() => {
    setConfirmTarget(null);
  }, []);

  return (
    <>
      <div className="rounded-xl border border-stone-200 bg-white">
        <div className="border-b border-stone-100 px-5 py-4">
          <h3 className="font-display text-base font-semibold text-stone-900">
            Actions
          </h3>
        </div>
        <div className="flex flex-wrap gap-2 p-5">
          {/* Status transition buttons */}
          {actions.map((action) => (
            <Button
              key={action.targetStatus}
              variant={action.variant}
              className={action.className}
              disabled={isUpdating}
              onClick={() => setConfirmTarget(action.targetStatus)}
            >
              {action.icon}
              <span className="ml-1.5">{t(action.labelKey)}</span>
            </Button>
          ))}

          {/* Universal actions */}
          <Button
            variant="outline"
            onClick={onPrintDeliverySlip}
            disabled={isUpdating}
          >
            <Printer className="h-4 w-4" />
            <span className="ml-1.5">{t("order_detail.print_delivery_slip")}</span>
          </Button>

          <Button
            variant="outline"
            onClick={onInitiateRefund}
            disabled={isUpdating}
          >
            <RotateCcw className="h-4 w-4" />
            <span className="ml-1.5">{t("order_detail.initiate_refund")}</span>
          </Button>
        </div>
      </div>

      {/* Confirmation modal */}
      <AdminModal
        open={confirmTarget !== null}
        onClose={handleCancel}
        title={t("order_detail.confirm_status_title")}
        footer={
          <>
            <Button variant="outline" onClick={handleCancel} disabled={isUpdating}>
              {t("order_detail.confirm_status_no")}
            </Button>
            <Button onClick={handleConfirm} disabled={isUpdating}>
              {isUpdating ? "..." : t("order_detail.confirm_status_yes")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-stone-600">
          {t("order_detail.confirm_status_message", {
            status: confirmTarget ? (STATUS_DISPLAY[confirmTarget] ?? confirmTarget) : "",
          })}
        </p>
      </AdminModal>
    </>
  );
}
